import {
  Button,
  Dialog,
  Heading,
  Paragraph,
} from "@digdir/designsystemet-react";
import { useBlocker } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  type BestillingPageKey,
  bestillingFormPageOrder,
} from "@/features/bestillinger";
import type { FileRouteTypes } from "@/routeTree.gen";
import { isDirty } from "@/utils";

type FormNavigationBlockerProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  onSubmit: (values: T) => Promise<unknown>;
};

export function FormNavigationBlocker<T extends FieldValues>({
  form,
  onSubmit: handleSubmit,
}: FormNavigationBlockerProps<T>) {
  const { t } = useTranslation();
  const formIsValid = form.formState.isValid;
  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: async ({ current, next }) => {
      const formIsDirty = isDirty(form.formState);
      if (!formIsDirty) return false;
      if (!formIsValid) {
        await form.trigger();
      }
      return getShouldBlockNext(current.fullPath, next.fullPath);
    },
    withResolver: true,
  });

  const primaryAction = useCallback(() => {
    if (status !== "blocked") return;
    if (!formIsValid) {
      proceed();
      return;
    }
    handleSubmit(form.getValues()).then(proceed).catch(reset);
  }, [proceed, status, formIsValid, handleSubmit, form.getValues, reset]);

  const secondaryAction = useCallback(() => {
    if (status !== "blocked") return;
    formIsValid ? proceed() : reset();
  }, [reset, status, formIsValid, proceed]);

  const dialogConfig = useMemo(() => {
    const title = formIsValid
      ? "skjema.navigering.kan-lagre.tittel"
      : "skjema.navigering.kan-ikke-lagre.tittel";
    const description = formIsValid
      ? "skjema.navigering.kan-lagre.beskrivelse"
      : "skjema.navigering.kan-ikke-lagre.beskrivelse";
    const primaryBtnText = formIsValid
      ? "skjema.navigering.kan-lagre.lagre"
      : "skjema.navigering.kan-ikke-lagre.videre";
    const secondaryBtnText = formIsValid
      ? "skjema.navigering.kan-lagre.ikke-lagre"
      : "skjema.navigering.kan-ikke-lagre.avbryt";

    return {
      title,
      description,
      primaryBtnText,
      secondaryBtnText,
    };
  }, [formIsValid]);

  if (status !== "blocked") {
    return null;
  }

  return (
    <Dialog className="space-y-4" open={true} onClose={() => reset()}>
      <Heading>{t(dialogConfig.title)}</Heading>
      <Paragraph>{t(dialogConfig.description)}</Paragraph>
      <div className="flex items-center gap-5">
        <Button onClick={primaryAction}>
          {t(dialogConfig.primaryBtnText)}
        </Button>
        <Button variant="secondary" onClick={secondaryAction}>
          {t(dialogConfig.secondaryBtnText)}
        </Button>
      </div>
    </Dialog>
  );
}

function getShouldBlockNext(
  currentPage: FileRouteTypes["fullPaths"] | "",
  nextPagePath: FileRouteTypes["fullPaths"] | "",
): boolean {
  const normalizedCurrentPath = normalizePath(currentPage);
  const currentPageIndex = bestillingFormPageOrder.indexOf(
    normalizedCurrentPath as BestillingPageKey,
  );
  if (currentPageIndex === -1) {
    return true;
  }

  const normalizedNextPath = normalizePath(nextPagePath);
  const expectedNextPage = bestillingFormPageOrder[currentPageIndex + 1];

  if (normalizedCurrentPath === normalizedNextPath) {
    return false; // Navigating to the same page, no need to block
  }

  // If there's no next page in sequence or we're navigating to an unexpected page
  return !expectedNextPage || expectedNextPage !== normalizedNextPath;
}

function normalizePath(path: FileRouteTypes["fullPaths"] | ""): string {
  // Remove trailing slash for consistent comparison
  return path.replace(/\/$/, "");
}
